import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './report.css';
import { Sankey } from './Sankey';
import { EventVsTotal, OverTime } from './OverTime';
import {
    DataQuality, FeatureTable, Findings, FunnelData, KpiRow, NavTable, NewUserFunnel, TopicViews,
    TotalsData,
} from './sections';
import { cssVar, useChart } from './useChart';

type Row = Record<string, unknown>;
interface Report {
    window: { from: string; to: string; days: number };
    previous: { from: string; to: string; days: number };
    generatedAt: string;
    editor: Row; viewer: Row;
    topicViews?: { rows?: Row[]; error?: string };
    totals?: TotalsData;
    findings: Array<{ scope: string; severity: string; text: string }>;
    errors: Array<{ section: string; error: string }>;
}

const DAY_MS = 86_400_000;
const todayIso = () => new Date().toISOString().slice(0, 10);
const shiftIso = (iso: string, days: number) =>
    new Date(new Date(`${iso}T00:00:00Z`).getTime() + days * DAY_MS).toISOString().slice(0, 10);
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const ProductReportPage: React.FC = () => {
    const [params, setParams] = useSearchParams();
    const to = DATE_RE.test(params.get('to') ?? '') ? params.get('to')! : todayIso();
    const from = DATE_RE.test(params.get('from') ?? '') ? params.get('from')! : shiftIso(to, -7);
    const compare = params.get('compare') !== '0';
    const days = Math.round(
        (new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / DAY_MS,
    );

    const [theme, setTheme] = useState<'light' | 'dark'>(() => {
        try {
            const t = localStorage.getItem('sonat-report-theme');
            return t === 'light' ? 'light' : 'dark';
        } catch { return 'dark'; }
    });
    const rootRef = useRef<HTMLDivElement>(null);
    const [report, setReport] = useState<Report | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [draft, setDraft] = useState<{ from: string; to: string }>({ from, to });
    const abortRef = useRef<AbortController | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    const setRange = useCallback((f: string, t: string) => {
        setParams((p) => {
            const next = new URLSearchParams(p);
            next.set('from', f);
            next.set('to', t);
            return next;
        }, { replace: true });
    }, [setParams]);

    useEffect(() => { setDraft({ from, to }); }, [from, to]);

    // One request per range; cancel in-flight on change so responses never
    // race into the DOM out of order.
    useEffect(() => {
        abortRef.current?.abort();
        const ctl = new AbortController();
        abortRef.current = ctl;
        setLoading(true);
        setError(null);
        fetch(`/api/product-report?from=${from}&to=${to}&seriesDays=90`, { signal: ctl.signal })
            .then(async (res) => {
                const body = await res.json().catch(() => ({}));
                if (!res.ok) {
                    throw new Error(body.error ?? body.message ?? `Request failed (${res.status})`);
                }
                setReport(body as Report);
            })
            .catch((err: Error) => {
                if (err.name !== 'AbortError') setError(err.message);
            })
            .finally(() => {
                if (!ctl.signal.aborted) setLoading(false);
            });
        return () => ctl.abort();
    }, [from, to]);

    const onDraftChange = (part: Partial<{ from: string; to: string }>) => {
        const next = { ...draft, ...part };
        setDraft(next);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (!DATE_RE.test(next.from) || !DATE_RE.test(next.to)) return;
            if (next.from >= next.to || next.to > todayIso()) return;
            const span = Math.round(
                (new Date(`${next.to}T00:00:00Z`).getTime() - new Date(`${next.from}T00:00:00Z`).getTime()) / DAY_MS,
            );
            if (span > 365) return;
            setRange(next.from, next.to);
        }, 500);
    };

    const toggleTheme = () => {
        const next = theme === 'dark' ? 'light' : 'dark';
        setTheme(next);
        try { localStorage.setItem('sonat-report-theme', next); } catch { /* private mode */ }
    };

    const editor = (report?.editor ?? {}) as Row;
    const viewer = (report?.viewer ?? {}) as Row;
    const ek = (editor.kpis ?? {}) as Row;
    const epk = (editor.prev_kpis ?? {}) as Row;
    const vk = (viewer.kpis ?? {}) as Row;
    const vpk = (viewer.prev_kpis ?? {}) as Row;
    const num = (v: unknown) => (typeof v === 'number' ? v : 0);

    const dau = (editor.dau ?? []) as Array<{ day: string; users: number }>;
    const dauRef = useChart(() => {
        if (!dau.length) return null;
        const accent = cssVar(rootRef.current, '--accent');
        const grid = cssVar(rootRef.current, '--chart-grid');
        const dim = cssVar(rootRef.current, '--fg-dim');
        return {
            type: 'line' as const,
            data: {
                labels: dau.map((r) => r.day),
                datasets: [{ label: 'DAU', data: dau.map((r) => r.users), borderColor: accent,
                    backgroundColor: `${accent}33`, fill: true, tension: 0.3, pointRadius: 2 }] as never,
            },
            options: {
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                    y: { beginAtZero: true, grid: { color: grid }, ticks: { color: dim } },
                    x: { grid: { display: false }, ticks: { color: dim } },
                },
            },
        };
    }, [dau, theme]);

    const presets = [7, 14, 30, 90];
    const presetActive = (n: number) => to === todayIso() && from === shiftIso(to, -n);
    const editorCategories = (editor.categories ?? []) as Array<[string, string[]]>;
    const evtLabels = useMemo(() => ['Google Docs created', 'Documents created', 'Topics created'], []);

    return (
        <div className="pr-root" data-theme={theme} ref={rootRef}>
            <div className="pr-wrap">
                <div className="pr-header">
                    <div>
                        <h1>Sonat product report</h1>
                        <div className="pr-meta">
                            {from} → {to} ({days} days, UTC)
                            {report && <> · generated {report.generatedAt}</>} · aggregate, no PII
                        </div>
                    </div>
                    <button type="button" className="pr-btn" onClick={toggleTheme}>Toggle theme</button>
                </div>

                <div className="pr-selector">
                    <span className="pr-ctl-label">Range</span>
                    <div className="pr-ctl-group">
                        {presets.map((n) => (
                            <button key={n} type="button"
                                className={`pr-chip ${presetActive(n) ? 'on' : ''}`}
                                disabled={loading}
                                onClick={() => setRange(shiftIso(todayIso(), -n), todayIso())}>
                                {n}d
                            </button>
                        ))}
                    </div>
                    <span className="pr-ctl-label">From</span>
                    <input className="pr-date" type="date" value={draft.from} max={draft.to}
                        onChange={(e) => onDraftChange({ from: e.target.value })} />
                    <span className="pr-ctl-label">To</span>
                    <input className="pr-date" type="date" value={draft.to} max={todayIso()}
                        onChange={(e) => onDraftChange({ to: e.target.value })} />
                    <label className="pr-check">
                        <input type="checkbox" checked={compare}
                            onChange={(e) => setParams((p) => {
                                const next = new URLSearchParams(p);
                                next.set('compare', e.target.checked ? '1' : '0');
                                return next;
                            }, { replace: true })} />
                        Compare with previous period
                    </label>
                    <span className="pr-resolved">
                        Window {from} → {to} · comparison {report?.previous
                            ? `${report.previous.from} → ${report.previous.to}`
                            : `${shiftIso(from, -days)} → ${from}`} · all boundaries UTC midnight
                        {loading && ' · loading…'}
                    </span>
                </div>

                {error && (
                    <div className="pr-error">
                        <strong>Report unavailable:</strong> {error}
                    </div>
                )}

                {report && (
                    <div className={loading ? 'pr-loading' : ''}>
                        <div className="pr-section-label">Editor — editor.sonat.com</div>
                        <KpiRow days={days} compare={compare} kpis={[
                            { label: 'Weekly active users', value: num(ek.wau), prev: num(epk.wau), accent: 'accent' },
                            { label: 'Active accounts', value: num(ek.accounts) },
                            { label: 'Paid accounts active', value: num(ek.paid_accounts) },
                            { label: 'Signups', value: num(ek.signups), prev: num(epk.signups) },
                            { label: 'Documents published', value: num(ek.publishes), prev: num(epk.publishes) },
                        ]} />
                        {dau.length > 0 && (
                            <div className="pr-card">
                                <h3>Daily active users (editor)</h3>
                                <div className="pr-chartbox tall"><canvas ref={dauRef} /></div>
                            </div>
                        )}
                        <NewUserFunnel funnel={editor.funnel as FunnelData | undefined} days={days} />
                        <div className="pr-card">
                            <h3>Feature usage — activity flow, all events split by new vs returning users</h3>
                            <p className="pr-caption">
                                Ribbon width = event count (unique users don't sum across branches — they
                                ride along in the tooltips). New = users first seen inside the window.
                            </p>
                            <Sankey graph={editor.sankey as never} theme={theme} rootRef={rootRef} />
                        </div>
                        <FeatureTable
                            title={`Feature usage — compare vs previous ${days} days`}
                            rows={(editor.features ?? []) as Row[]}
                            prevRows={(editor.features_prev ?? []) as Row[]}
                            days={days} compare={compare}
                            caption="New = users first seen inside the window. Click a header to sort."
                        />
                        <OverTime
                            series={editor.series as never} seriesDays={num(editor.series_days) || 90}
                            categories={editorCategories} theme={theme} rootRef={rootRef}
                        />
                        <EventVsTotal
                            pairsBase={(editor.event_vs_total ?? []) as Row[]}
                            pairsSeries={(editor.event_vs_total_series ?? []) as Row[]}
                            seriesByPlan={(editor.series_by_plan ?? []) as never}
                            pairLabels={evtLabels}
                            series={editor.series as never} seriesDays={num(editor.series_days) || 90}
                            theme={theme} rootRef={rootRef}
                            totals={report.totals}
                            categories={editorCategories}
                        />
                        <NavTable rows={(editor.nav ?? []) as Row[]} />

                        <div className="pr-section-label viewer">Viewer — sonat.com</div>
                        <KpiRow days={days} compare={compare} kpis={[
                            { label: 'Readers (unique)', value: num(vk.readers), prev: num(vpk.readers), accent: 'viewer' },
                            { label: 'Searches', value: num(vk.searches), prev: num(vpk.searches) },
                            { label: 'AI questions', value: num(vk.ai_asks), prev: num(vpk.ai_asks) },
                            { label: 'PDF downloads', value: num(vk.pdf_downloads) },
                        ]} />
                        <div className="pr-card">
                            <h3>Viewer activity flow — all events split by new vs returning visitors</h3>
                            <Sankey graph={viewer.sankey as never} theme={theme} rootRef={rootRef} />
                        </div>
                        <FeatureTable
                            title="Viewer feature usage"
                            rows={(viewer.features ?? []) as Row[]}
                            days={days} compare={false}
                            caption="New = visitors first seen inside the window."
                        />
                        <TopicViews data={report.topicViews} />
                        <Findings findings={report.findings ?? []} />
                        <DataQuality errors={report.errors ?? []} />
                        <div className="pr-footer">
                            Numbers come straight from PostHog / BigQuery — an empty section means the
                            event is unused or newly instrumented, not zero-by-design.
                        </div>
                    </div>
                )}
                {!report && !error && <div className="pr-empty">Loading report…</div>}
            </div>
        </div>
    );
};

export default ProductReportPage;
